import { BarChart3, IndianRupee } from 'lucide-react'
import * as React from 'react'

import { Link } from 'react-router'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '#app/components/ui/navigation-menu'
import { useRootLoader } from '#app/utils/app.ts'
import { cn } from '#app/utils/misc.tsx'
import { userHasPermission, type PermissionString } from '#app/utils/user'

// Define navigation items with their permissions
interface NavItem {
	title: string
	href: string
	description: string
	permission: PermissionString
}

// Define navigation sections
interface NavSection {
	title: string
	items: Array<NavItem & { className?: string }>
	dashboardLink?: {
		href: string
		title: string
		description: string
		permission: PermissionString
	}
}

// Define navigation sections
const navSections: Record<string, NavSection> = {
	accounting: {
		title: 'Accounting',
		items: [
			{
				title: 'Make Payment',
				href: '/accounting/payment',
				description: 'Add payment entry to accounts.',
				permission: 'create:payment:any' as PermissionString,
			},
			{
				title: 'Receive Payment',
				href: '/accounting/receipt',
				description: 'Add receipt entry to accounts.',
				permission: 'create:receipt:any' as PermissionString,
			},
			{
				title: 'Chart of Accounts',
				href: '/accounting/chart-of-accounts',
				description: 'View and create accounts and account groups.',
				permission: 'read:chartOfAccounts:any' as PermissionString,
			},
			{
				title: 'Financial Reports',
				href: '/reports/finances',
				description: 'Access comprehensive financial reports',
				permission: 'read:financesReport:any' as PermissionString,
				className: 'col-span-2',
			},
		],
		dashboardLink: {
			href: '/accounting',
			title: 'Accounting Dashboard',
			description: 'Financial overview and metrics',
			permission: 'access:accountingDashboard:any' as PermissionString,
		},
	},
	reports: {
		title: 'Reports',
		items: [
			{
				title: 'Fee Reports',
				href: '/reports/fees',
				description: 'Complete reports for fee related transactions.',
				permission: 'read:feesReport:any' as PermissionString,
				className: 'col-span-2',
			},
			{
				title: 'Financial Reports',
				href: '/reports/finances',
				description: "Complete reports of the school's financial position.",
				permission: 'read:financesReport:any' as PermissionString,
				className: 'col-span-2',
			},
		],
	},
	students: {
		title: 'Students',
		dashboardLink: {
			href: '/students',
			title: 'Students Dashboard',
			description: 'Students overview',
			permission: 'access:studentsPage:any' as PermissionString,
		},
		items: [
			{
				title: 'Student Admission',
				href: '/students/admission',
				description: 'New Student Admission',
				permission: 'create:student:any' as PermissionString,
				className: 'col-span-2',
			},
			{
				title: 'Student Promotion',
				href: '/students/promotions',
				description: 'Promote students to the next class',
				permission: 'update:studentPromotion:any' as PermissionString,
			},
		],
	},
}

export function AppNavigationMenu() {
	const { user, isAdmin } = useRootLoader()

	// Filter items based on user permissions
	const getFilteredSectionItems = (sectionKey: keyof typeof navSections) => {
		const section = navSections[sectionKey]
		if (!section) return []

		return section.items.filter((item) => {
			// Allow all items for admin users
			if (isAdmin) return true
			// Otherwise check specific permission
			return userHasPermission(user, item.permission)
		})
	}

	// Check if section should be displayed
	const shouldShowSection = (sectionKey: keyof typeof navSections) => {
		const section = navSections[sectionKey]
		if (!section) return false

		// Admin always sees all sections
		if (isAdmin) return true

		// For non-admins, show section if they have permission for any item
		const hasItemPermission = section.items.some((item) =>
			userHasPermission(user, item.permission),
		)

		// For sections with dashboard, also check dashboard permission
		if (section.dashboardLink) {
			return (
				hasItemPermission ||
				userHasPermission(user, section.dashboardLink.permission)
			)
		}

		return hasItemPermission
	}

	// Check if dashboard link should be shown
	const canShowDashboard = (sectionKey: keyof typeof navSections) => {
		const section = navSections[sectionKey]
		if (!section || !section.dashboardLink) return false

		return isAdmin || userHasPermission(user, section.dashboardLink.permission)
	}

	return (
		<NavigationMenu>
			<NavigationMenuList>
				{shouldShowSection('accounting') && (
					<NavigationMenuItem>
						<NavigationMenuTrigger>Accounting</NavigationMenuTrigger>
						<NavigationMenuContent>
							<ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
								{canShowDashboard('accounting') &&
									navSections.accounting?.dashboardLink && (
										<li className="row-span-4">
											<NavigationMenuLink asChild>
												<a
													className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-gradient-to-b p-6 no-underline outline-none select-none focus:shadow-md"
													href={
														navSections.accounting?.dashboardLink?.href || ''
													}
												>
													<BarChart3 className="h-8 w-8" />
													<div className="mt-4 mb-2 text-lg font-medium">
														{navSections.accounting?.dashboardLink?.title}
													</div>
													<p className="text-muted-foreground text-sm leading-tight">
														{navSections.accounting?.dashboardLink?.description}
													</p>
												</a>
											</NavigationMenuLink>
										</li>
									)}

								{getFilteredSectionItems('accounting').map((item) => (
									<ListItem
										key={item.href}
										href={item.href}
										title={item.title}
										className={item.className}
									>
										{item.description}
									</ListItem>
								))}
							</ul>
						</NavigationMenuContent>
					</NavigationMenuItem>
				)}

				{shouldShowSection('reports') && (
					<NavigationMenuItem>
						<NavigationMenuTrigger>Reports</NavigationMenuTrigger>
						<NavigationMenuContent>
							<ul
								className={cn(
									'grid gap-3 p-4',
									isAdmin
										? 'w-[400px] md:w-[500px] md:grid-cols-2 lg:w-[600px]'
										: 'w-[200px] grid-cols-1 md:w-[250px] lg:w-[300px]',
								)}
							>
								{getFilteredSectionItems('reports').map((item) => (
									<ListItem
										key={item.href}
										href={item.href}
										title={item.title}
										className={item.className}
									>
										{item.description}
									</ListItem>
								))}
							</ul>
						</NavigationMenuContent>
					</NavigationMenuItem>
				)}

				{shouldShowSection('students') && (
					<NavigationMenuItem>
						<NavigationMenuTrigger>Students</NavigationMenuTrigger>
						<NavigationMenuContent>
							<ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
								{canShowDashboard('students') &&
									navSections.students?.dashboardLink && (
										<li className="row-span-4">
											<NavigationMenuLink asChild>
												<a
													className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-gradient-to-b p-6 no-underline outline-none select-none focus:shadow-md"
													href={navSections.students?.dashboardLink?.href || ''}
												>
													<BarChart3 className="h-8 w-8" />
													<div className="mt-4 mb-2 text-lg font-medium">
														{navSections.students?.dashboardLink?.title}
													</div>
													<p className="text-muted-foreground text-sm leading-tight">
														{navSections.students?.dashboardLink?.description}
													</p>
												</a>
											</NavigationMenuLink>
										</li>
									)}
								{getFilteredSectionItems('students').map((item) => (
									<ListItem
										key={item.href}
										href={item.href}
										title={item.title}
										className={item.className}
									>
										{item.description}
									</ListItem>
								))}
							</ul>
						</NavigationMenuContent>
					</NavigationMenuItem>
				)}

				<NavigationMenuItem>
					<Link to="/settings">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							Settings
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>

				<NavigationMenuItem>
					<Link to="/">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							Home
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	)
}

const ListItem = React.forwardRef<
	React.ElementRef<'a'>,
	React.ComponentPropsWithoutRef<'a'>
>(({ className, title, children, href, ...props }, ref) => {
	return (
		<li>
			<NavigationMenuLink asChild>
				<Link
					ref={ref}
					to={href ?? ''}
					className={cn(
						'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block space-y-1 rounded-md p-3 leading-none no-underline transition-colors outline-none select-none',
						className,
					)}
					{...props}
				>
					<div className="text-sm leading-none font-medium">{title}</div>
					<p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
						{children}
					</p>
				</Link>
			</NavigationMenuLink>
		</li>
	)
})
ListItem.displayName = 'ListItem'
